import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { PictureInPicture as PictureInPictureIcon } from "lucide-react";
import { IconButton, Tooltip } from "@mui/material";
import PictureInPicture, {
  ReactDocumentPictureInPictureForwardRefType,
} from "../../common/picture-in-picture/PictureInPicture";
import HttpErrorProvider from "./HttpErrorProvider";
import {
  DocumentPicture,
  DocumentPictureProps,
  BasePictureProps,
} from "components/picture-in-picture/DocumentPicture";

type PictureProps = DocumentPictureProps;

// Picture component mapping
const PictureComponents = {
  document: DocumentPicture,
  // Add more components here as needed
  // task: TaskPicture,
} as const;

interface PictureInPictureContentContextProps {
  isPipContentOpen: boolean;
  setPipContentOpen: (isOpen: boolean) => void;
  pipContentProps: PictureProps | null;
  setPipContentProps: (props: PictureProps | null) => void;
  openPip: (props: PictureProps) => void;
  closePip: () => void;
  togglePip: () => void;
  PipButton: () => JSX.Element;
  isOpen: boolean;
  contentRef: React.RefObject<HTMLElement>;
}

const PictureInPictureContentContext = createContext<
  PictureInPictureContentContextProps | undefined
>(undefined);

interface PictureInPictureContentProviderProps {
  children: ReactNode;
  width?: string | number;
  height?: string | number;
  onOpen?: () => void;
  onClose?: () => void;
  onResize?: (width: number, height: number) => void;
  contentRef?: React.RefObject<HTMLElement>;
  id?: string;
}

export const PictureInPictureContentProvider = ({
  children,
  width = "50%",
  height = "50%",
  onOpen,
  onClose,
  onResize,
  contentRef,
  id,
}: PictureInPictureContentProviderProps) => {
  const [isPipContentOpen, setPipContentOpen] = useState(false);
  const [pipContentProps, setPipContentProps] = useState<PictureProps | null>(
    null,
  );
  const pipRef = useRef<ReactDocumentPictureInPictureForwardRefType>(null);
  const latestPropsRef = useRef<PictureProps | null>(pipContentProps);

  // Update the ref whenever props change
  useEffect(() => {
    latestPropsRef.current = pipContentProps;
  }, [pipContentProps]);

  const openPip = useCallback((props: PictureProps) => {
    setPipContentProps(props);
    setPipContentOpen(true);
    pipRef.current?.toggle();
  }, []);

  const closePip = useCallback(() => {
    setPipContentOpen(false);
    pipRef.current?.close();
  }, []);

  const togglePip = useCallback(() => {
    pipRef.current?.toggle();
  }, []);

  const handleClose = useCallback(() => {
    setPipContentOpen(false);
    onClose?.();
  }, [onClose]);

  const renderPictureContent = useCallback(() => {
    if (!latestPropsRef.current) {
      return null;
    }

    const { pictureName } = latestPropsRef.current;
    const PictureComponent = PictureComponents[pictureName];

    if (!PictureComponent) {
      console.error(`Unknown picture type: ${pictureName}`);
      return null;
    }

    return <PictureComponent {...latestPropsRef.current} />;
  }, []);

  const PipButton = useCallback(() => {
    return (
      <PictureInPicture
        ref={pipRef}
        width={width}
        height={height}
        onOpen={onOpen}
        onClose={handleClose}
        onResize={onResize}
        buttonRenderer={({ toggle }) => (
          <Tooltip title="Open in picture-in-picture">
            <IconButton onClick={toggle}>
              <PictureInPictureIcon color="#8E9598" size={20} />
            </IconButton>
          </Tooltip>
        )}
        contentRef={contentRef}
        id={id}
      >
        <HttpErrorProvider>{renderPictureContent()}</HttpErrorProvider>
      </PictureInPicture>
    );
  }, [
    width,
    height,
    onOpen,
    handleClose,
    onResize,
    contentRef,
    id,
    renderPictureContent,
  ]);

  return (
    <PictureInPictureContentContext.Provider
      value={{
        isPipContentOpen,
        setPipContentOpen,
        pipContentProps,
        setPipContentProps,
        openPip,
        closePip,
        togglePip,
        PipButton,
        isOpen: pipRef.current?.isOpen ?? false,
        contentRef: contentRef!,
      }}
    >
      {children}
    </PictureInPictureContentContext.Provider>
  );
};

export const usePictureInPictureContentContext = () => {
  const context = useContext(PictureInPictureContentContext);
  if (!context) {
    throw new Error(
      "usePictureInPictureContentContext must be used within a PictureInPictureContentProvider",
    );
  }
  return context;
};

// Export types for external use
export type { PictureProps, BasePictureProps };
